import { useState } from "react";
import { toast } from "sonner";
import { RevealBlock, ClipReveal } from "./motion";

const FORM_NAME = "airport-enquiry";

const ROLES = [
  "Airport leasing",
  "Property or facility manager",
  "Brand or supplier",
  "Machine or technology provider",
  "University or education",
  "Student accommodation",
  "Hospital or healthcare",
  "Workplace or commercial property",
  "Other",
];

const LOCATIONS = [
  "Airport",
  "Hospital",
  "University",
  "Student accommodation",
  "Workplace",
  "Transport hub",
  "Shopping centre",
  "Other high-traffic location",
];

const IMG = "/machine.png";

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  role: "",
  location: "",
  message: "",
  website: "", // honeypot
};

export default function Enquiries() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name || form.name.trim().length < 2) errs.name = "Please enter your name.";
    if (!form.company || form.company.trim().length < 1) errs.company = "Please enter your company.";
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email address.";
    if (!form.message || form.message.trim().length < 5) errs.message = "Please tell us a little about your enquiry.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      const body = new URLSearchParams({ "form-name": FORM_NAME, ...form });
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!response.ok) throw new Error(`Form submission failed with ${response.status}`);
      setSubmitted(true);
      setForm(initial);
      toast.success("Thank you for your interest in MinisRus.");
    } catch {
      toast.error("We couldn't submit your enquiry. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquiries" className="relative bg-ink py-16 md:py-24 border-t border-white/8" data-testid="enquiries-section">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-8 md:gap-12 items-start">
        {/* Left image + copy */}
        <div className="col-span-12 lg:col-span-5">
          <ClipReveal className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-ink2 border border-white/8">
            <img src={IMG} alt="MinisRus TCN-ELV-11G automated retail machine" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
          </ClipReveal>
          <RevealBlock delay={0.1}>
            <h2 className="mt-8 font-display font-light text-bone text-3xl md:text-4xl leading-tight">
              Let&apos;s build a<br /><span className="text-orange">focused pilot.</span>
            </h2>
            <p className="mt-4 text-bone/70 text-base leading-relaxed max-w-md font-sans">
              Airport, university, accommodation and property teams can request a site assessment or pilot discussion. Tell us about the location, audience need and operating requirements.
            </p>
          </RevealBlock>
        </div>

        {/* Right form card */}
        <RevealBlock delay={0.15} className="col-span-12 lg:col-span-7">
          {submitted ? (
            <div
              className="bg-ink2 border border-orange/40 rounded-2xl p-8 md:p-12"
              data-testid="enquiries-success"
              role="status"
              aria-live="polite"
            >
              <div className="w-14 h-14 rounded-full bg-orange/15 flex items-center justify-center mb-6">
                <span className="text-orange text-2xl">✓</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-bone leading-tight">Enquiry received.</h3>
              <p className="mt-4 text-bone/75 text-base md:text-lg leading-relaxed max-w-lg font-sans">
                Thank you for your interest in MinisRus. Our team will review your enquiry and respond shortly.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="mt-8 inline-flex items-center gap-2 h-11 px-6 border border-bone/30 hover:border-orange text-bone hover:text-orange rounded-full text-[13px] font-medium transition-colors"
                data-testid="submit-another-btn"
              >
                Submit another enquiry
              </button>
            </div>
          ) : (
            <form
              name={FORM_NAME}
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="website"
              onSubmit={onSubmit}
              className="bg-ink2 border border-white/8 rounded-2xl p-6 md:p-10 space-y-5"
              data-testid="enquiries-form"
              noValidate
            >
              <input type="hidden" name="form-name" value={FORM_NAME} />
              <h3 className="font-display text-2xl md:text-3xl text-bone">Site &amp; Pilot Enquiry</h3>
              <p className="text-bone/65 text-sm leading-relaxed">
                We use the details you provide only to assess and respond to your enquiry. Please do not include health or other sensitive personal information. Read our{" "}
                <a href="/privacy/" className="text-orange hover:text-orange-soft link-underline">privacy policy</a>.
              </p>

              {/* Honeypot — visually hidden, must remain empty */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                <label>Website (leave empty)
                  <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} name="website" />
                </label>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your Name*" error={errors.name}>
                  <Input name="name" value={form.name} onChange={update("name")} testId="input-name" autoComplete="name" aria-invalid={Boolean(errors.name)} required />
                </Field>
                <Field label="Company / Organisation*" error={errors.company}>
                  <Input name="company" value={form.company} onChange={update("company")} testId="input-company" autoComplete="organization" aria-invalid={Boolean(errors.company)} required />
                </Field>
                <Field label="Email Address*" error={errors.email}>
                  <Input name="email" type="email" value={form.email} onChange={update("email")} testId="input-email" autoComplete="email" aria-invalid={Boolean(errors.email)} required />
                </Field>
                <Field label="Phone Number">
                  <Input name="phone" type="tel" value={form.phone} onChange={update("phone")} testId="input-phone" autoComplete="tel" />
                </Field>
                <Field label="Your Role">
                  <Select name="role" value={form.role} onChange={update("role")} options={ROLES} placeholder="Select your role" testId="input-role" />
                </Field>
                <Field label="Location Type">
                  <Select name="location" value={form.location} onChange={update("location")} options={LOCATIONS} placeholder="Select location type" testId="input-location" />
                </Field>
              </div>

              <Field label="Message*" error={errors.message}>
                <textarea
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your location, audience need and operating requirements"
                  className="w-full bg-ink border border-white/12 focus:border-orange rounded-md px-4 py-3 text-bone placeholder-bone/40 outline-none resize-none transition-colors text-sm"
                  data-testid="input-message"
                  aria-invalid={Boolean(errors.message)}
                  required
                />
              </Field>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-2 h-12 px-8 bg-orange text-ink hover:bg-orange-soft rounded-full text-[13px] font-semibold transition-colors disabled:opacity-60"
                  data-testid="submit-enquiry-btn"
                >
                  {loading ? "Sending…" : "Request Pilot Discussion"}
                  <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                </button>
              </div>
            </form>
          )}
        </RevealBlock>
      </div>
    </section>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="block text-bone/70 text-[12px] uppercase tracking-[0.14em] mb-2">{label}</span>
      {children}
      {error && <span className="mt-1 block text-red-400/90 text-xs" role="alert">{error}</span>}
    </label>
  );
}

function Input({ name, value, onChange, type = "text", testId, required, ...props }) {
  return (
    <input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      {...props}
      className="w-full bg-ink border border-white/12 focus:border-orange rounded-md px-4 h-12 text-bone placeholder-bone/40 outline-none transition-colors text-sm"
      data-testid={testId}
    />
  );
}

function Select({ name, value, onChange, options, placeholder, testId }) {
  return (
    <div className="relative">
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full appearance-none bg-ink border border-white/12 focus:border-orange rounded-md pl-4 pr-10 h-12 text-bone outline-none transition-colors text-sm cursor-pointer"
        data-testid={testId}
      >
        <option value="" disabled className="bg-ink text-bone/60">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o} className="bg-ink text-bone">{o}</option>
        ))}
      </select>
      <span aria-hidden="true" className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-orange text-xs">▾</span>
    </div>
  );
}
